
import { DetectedLocation, DetectedMultipleLocations } from '../mappings/LocationMapping';

export const applyLocationFilter = (dbQuery: any, detectedLocation: DetectedLocation | DetectedMultipleLocations) => {
  // Handle multiple locations
  if ('locations' in detectedLocation) {
    return applyMultipleLocationFilter(dbQuery, detectedLocation);
  }
  
  // Handle single location (existing logic)
  const { type, info } = detectedLocation;
  
  console.log('=== APPLYING HIERARCHICAL LOCATION FILTER ===');
  console.log('Filter type:', type);
  console.log('Filter info:', JSON.stringify(info, null, 2));
  
  if (type === 'city') {
    console.log('*** APPLYING CITY FILTER (HIGHEST PRIORITY) ***');
    console.log('City:', info.city, 'Region:', info.region, 'Country:', info.country);
    
    // DEBUG: Special handling for KOS
    if (info.city === 'Kos') {
      console.log('🚨 === KOS CITY FILTER DEBUG ===');
      console.log('🚨 Applying STRICT filter for Kos (Greek island)');
      console.log('🚨 Expected country: Griekenland');
      console.log('🚨 Expected city: Kos');
      console.log('🚨 This should ONLY return results from Greece with Kos as region OR city');
    }
    
    // For city search, be STRICT about city/region matching
    // Don't search in name/description to avoid false matches
    const result = dbQuery
      .eq('country', info.country)
      .or(`city.ilike.%${info.city}%,region.ilike.%${info.city}%`);
    
    console.log('🎯 Applied STRICT city filter (city OR region only):', {
      country: info.country,
      city: info.city
    });
    return result;
    
  } else if (type === 'region') {
    console.log('*** APPLYING REGION FILTER (MEDIUM PRIORITY) ***');
    console.log('Region:', info.region, 'Country:', info.country);
    
    // Special handling for Costa Brava and other popular regions
    if (info.region === 'Costa Brava') {
      console.log('🏖️ Special Costa Brava handling - very broad search');
      const result = dbQuery
        .eq('country', info.country)
        .or('region.ilike.%Costa Brava%,city.ilike.%Costa Brava%,description.ilike.%Costa Brava%,name.ilike.%Costa Brava%,facilities.ilike.%Costa Brava%');
      
      console.log('🎯 Applied VERY BROAD Costa Brava filter');
      return result;
    }
    
    // For other regions, use flexible matching
    const result = dbQuery
      .eq('country', info.country)
      .or(`region.ilike.%${info.region}%,city.ilike.%${info.region}%,description.ilike.%${info.region}%,name.ilike.%${info.region}%`);
    
    console.log('🎯 Applied FLEXIBLE region filter:', {
      country: info.country,
      region: info.region
    });
    return result;
    
  } else if (type === 'country') {
    console.log('*** APPLYING COUNTRY FILTER (LOWEST PRIORITY) ***');
    console.log('Target country:', info.country);
    
    // For country search, show all results from that country
    const result = dbQuery.eq('country', info.country);
    
    console.log('🎯 Applied country filter:', {
      country: info.country
    });
    return result;
  }
  
  console.log('❌ NO LOCATION FILTER APPLIED - this should not happen!');
  return dbQuery;
};

// Function to apply coastal exclusions ONLY for Netherlands
export const applyCoastalExclusionsForCountry = (dbQuery: any, searchTerms: string[], country: string) => {
  const hasCoastalTerms = searchTerms.some(term => {
    const lowerTerm = term.toLowerCase();
    return lowerTerm.includes('kust') || lowerTerm.includes('strand') || lowerTerm.includes('zee');
  });

  // Only apply for Netherlands and only with coastal terms
  if (hasCoastalTerms && country === 'Nederland') {
    console.log('Applying coastal exclusions for Netherlands coastal search');
    
    return dbQuery
      .not('region', 'ilike', '%limburg%')
      .not('region', 'ilike', '%brabant%')
      .not('region', 'ilike', '%gelderland%')
      .not('region', 'ilike', '%utrecht%')
      .not('region', 'ilike', '%overijssel%')
      .not('region', 'ilike', '%drenthe%')
      .not('region', 'ilike', '%flevoland%')
      .not('city', 'ilike', '%maastricht%')
      .not('city', 'ilike', '%eindhoven%')
      .not('city', 'ilike', '%utrecht%')
      .not('city', 'ilike', '%arnhem%')
      .not('city', 'ilike', '%nijmegen%')
      .not('city', 'ilike', '%apeldoorn%')
      .not('city', 'ilike', '%enschede%')
      .not('city', 'ilike', '%zwolle%')
      .not('city', 'ilike', '%deventer%')
      .not('city', 'ilike', '%amersfoort%')
      .not('description', 'ilike', '%meer%')
      .not('description', 'ilike', '%lake%')
      .not('name', 'ilike', '%meer%')
      .not('name', 'ilike', '%lake%');
  }
  
  return dbQuery;
};

// Function to handle multiple location filters (OR logic)
const applyMultipleLocationFilter = (dbQuery: any, multipleLocations: DetectedMultipleLocations) => {
  console.log('=== APPLYING MULTIPLE LOCATION FILTER ===');
  console.log('Multiple locations:', multipleLocations);
  
  if (multipleLocations.type === 'multiple_cities') {
    console.log('🎯 Building OR filter for multiple cities');
    
    // Build individual location filters and combine with OR
    let orQuery = '';
    multipleLocations.locations.forEach((location, index) => {
      const info = location.info;
      console.log(`Building condition ${index + 1} for city:`, info.city, 'in', info.country);
      
      // Each condition: correct country AND (city OR region matches)
      const condition = `and(country.eq.${info.country},or(city.ilike.%${info.city}%,region.ilike.%${info.city}%))`;
      
      if (index === 0) {
        orQuery = condition;
      } else {
        orQuery += `,${condition}`;
      }
    });
    
    console.log('🎯 Final OR condition:', orQuery);
    const result = dbQuery.or(orQuery);
    console.log('🎯 Applied MULTIPLE CITY filter with OR logic');
    return result;
  }
  
  console.log('❌ Unsupported multiple location type');
  return dbQuery;
};
